import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useConstant } from "@hooks/useConstant";
import { Drawer } from "@design-system/Drawer/Drawer";
import { Loader } from "@design-system/Loader/Loader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { Pagination } from "@design-system/Pagination/Pagination";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { ArrowCornerIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useEntitiesById } from "@hooks/useEntities";
import { useOverlays } from "contexts/OverlayContext";
import {
    AuditLogChangeType,
    HistoryDetailsItem,
    isHistoryDetailsResponse,
} from "@common/types/auditLog";
import { useGetHistoryDetailsQuery } from "@api/auditLog.api";
import { useHistoryDetailsListParams } from "../useHistoryDetailsListParams";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    getShortJournalEntryReferenceNumber,
    JournalEntrySourceTypeLabels,
} from "@common/helpers/accounting";
import { JournalEntrySourceType } from "@common/schemas/domains/accounting/journalEntry";
import { View } from "@common/types/views/views";
import { getSourceTypeTemplate } from "@common/constants/history-source-types";
import { StepTrailBreadcrumbs } from "@components/breadcrumbs/StepTrailBreadcrumbs";
import { historyDrawerContext } from "../../historyDrawerContext";
import { getShowRevertedParam } from "../../views/historyViewConfig";
import {
    resolveChangeTypeVerb,
    shouldOverrideIntegrationVerb,
} from "../../utils/getActivityDescription";
import { groupByKey } from "../../utils/groupByKey";
import { getBatchKey } from "../../utils/getBatchKey";
import { ActivityDrawerHeader } from "../ActivityDrawerHeader";
import {
    journalEntryListViewConfig,
    JournalEntryListRow,
} from "./views/journalEntryListViewConfig";
import {
    journalEntryVersionsViewConfig,
    JournalEntryVersionRow,
} from "./views/journalEntryVersionsViewConfig";
import {
    defaultJournalEntryListView,
    defaultJournalEntryVersionsView,
} from "./views/defaultJournalEntryViews";
import { journalEntryMetadataSchema } from "./schema";
import { HistoryJournalEntryDetails } from "./HistoryJournalEntryDetails";
import styles from "./JournalEntryHistoryDrawer.module.scss";

const JOURNAL_ENTRY_BREADCRUMB_STEPS = [
    { id: "parent" as const, label: "Journal entries" },
    { id: "versions" as const, label: "Versions" },
    { id: "details" as const, label: "Details" },
];

// `excludeFrom1099` is a per-line flag on each `journal_entry` row, so a single
// audit-log resource only carries the value for its own line. Derive the
// journal-entry-level flag by treating the entry as excluded when any of its
// line logs is excluded.
function isLogExcludedFrom1099(log: HistoryDetailsItem): boolean {
    const meta = journalEntryMetadataSchema.safeParse(log.resource ?? {});
    return meta.success && meta.data.excludeFrom1099 === true;
}

function extractJournalEntryData(
    log: HistoryDetailsItem,
    { preferDiffLines = false }: { preferDiffLines?: boolean } = {},
) {
    const resource = log.resource;
    const meta = journalEntryMetadataSchema.safeParse(resource ?? {});
    const date =
        meta.success && meta.data.date
            ? meta.data.date
            : new Date(log.createdAt);
    const dateOnly =
        meta.success && meta.data.dateOnly ? meta.data.dateOnly : null;
    const referenceNumber = meta.success
        ? (meta.data.referenceNumber ?? log.resourceId)
        : log.resourceId;
    const reference = getShortJournalEntryReferenceNumber(referenceNumber);

    let source =
        JournalEntrySourceTypeLabels[JournalEntrySourceType.ManualJournalEntry];
    if (resource?.transactionId) {
        source =
            JournalEntrySourceTypeLabels[JournalEntrySourceType.Transaction];
    } else if (resource?.openingBalanceId) {
        source =
            JournalEntrySourceTypeLabels[JournalEntrySourceType.OpeningBalance];
    } else if (resource?.loanId) {
        source = JournalEntrySourceTypeLabels[JournalEntrySourceType.Loan];
    }

    const linesDiff = log.diff.find((d) => d.field === "lines");
    const afterLines = linesDiff?.after?.value;
    const diffLines =
        Array.isArray(afterLines) && afterLines.length > 0
            ? afterLines
            : linesDiff?.before?.value;

    let lines: unknown[] = [];
    if (preferDiffLines && Array.isArray(diffLines)) {
        lines = diffLines;
    } else if (Array.isArray(resource?.lines) && resource.lines.length > 0) {
        lines = resource.lines;
    } else if (Array.isArray(diffLines)) {
        lines = diffLines;
    }

    const amount = (lines as Array<Record<string, unknown>>).reduce(
        (sum, line) => sum + (Number(line.debitAmount) || 0),
        0,
    );

    return {
        date,
        dateOnly,
        reference,
        source,
        amount,
        originalAmount: amount,
        linesCount: lines.length,
    };
}

export const JournalEntryHistoryDrawer: React.FC = () => {
    const { open, historyRow, closeDrawer } = useContext(historyDrawerContext);

    const [isOpen, setIsOpen] = useState(false);
    const [parentSearch, setParentSearch] = useState("");
    const [versionsSearch, setVersionsSearch] = useState("");

    const [selectedSourceId, setSelectedSourceId] = useState<string | null>(
        null,
    );
    const [selectedBatchKey, setSelectedBatchKey] = useState<string | null>(
        null,
    );

    const [parentView, setParentView] = useState<View>(
        defaultJournalEntryListView,
    );
    const [versionsView, setVersionsView] = useState<View>(
        defaultJournalEntryVersionsView,
    );

    const [parentPage, setParentPage] = useState(1);
    const [versionsPage, setVersionsPage] = useState(1);

    const listParams = useHistoryDetailsListParams(historyRow);
    const fallbackDate = useConstant(() => new Date());

    const shouldShowContent = isOpen || open;

    const { data: allLogsResponse, isLoading: isParentsLoading } =
        useGetHistoryDetailsQuery({
            ...listParams,
            date: historyRow?.date ?? fallbackDate,
            sourceType: historyRow?.sourceType ?? "",
            sourceIds: historyRow?.historyItem.sourceIds,
            actorId: historyRow?.actorId,
            organizationActorId: historyRow?.historyItem.organizationActorId,
            changeTypes: historyRow?.changeTypes,
            page: parentPage,
            limit: 100,
            showReverted: getShowRevertedParam(historyRow),
            options: {
                enabled:
                    shouldShowContent &&
                    !!historyRow &&
                    !!historyRow.sourceType,
            },
        });

    const allLogs = useMemo<HistoryDetailsItem[]>(() => {
        if (!allLogsResponse || !isHistoryDetailsResponse(allLogsResponse)) {
            return [];
        }
        return allLogsResponse.data;
    }, [allLogsResponse]);

    const parentTotalPages = useMemo(() => {
        if (!allLogsResponse) {
            return 1;
        }
        if (isHistoryDetailsResponse(allLogsResponse)) {
            return allLogsResponse.totalPages;
        }
        return 1;
    }, [allLogsResponse]);

    const logsBySourceId = useMemo(
        () =>
            groupByKey(
                allLogs,
                (log) => log.sourceId ?? log.resourceId ?? "unknown",
            ),
        [allLogs],
    );

    const parentRows = useMemo<JournalEntryListRow[]>(() => {
        const rows: JournalEntryListRow[] = [];
        for (const [sourceId, logs] of logsBySourceId.entries()) {
            if (sourceId === "unknown") {
                continue;
            }
            const data = extractJournalEntryData(logs[0]);
            rows.push({
                id: sourceId,
                sourceId,
                date: data.date,
                dateOnly: data.dateOnly,
                reference: data.reference,
                source: data.source,
                amount: data.amount,
                originalAmount: data.originalAmount,
                excludeFrom1099: logs.some(isLogExcludedFrom1099),
                entityId: logs[0].entityId ?? null,
            });
        }
        return rows;
    }, [logsBySourceId]);

    const filteredParentRows = useMemo(() => {
        if (!parentSearch) {
            return parentRows;
        }
        const term = parentSearch.toLowerCase();
        return parentRows.filter(
            (row) =>
                row.reference.toLowerCase().includes(term) ||
                row.source.toLowerCase().includes(term),
        );
    }, [parentRows, parentSearch]);

    const { data: versionsResponse, isLoading: isVersionsLoading } =
        useGetHistoryDetailsQuery({
            ...listParams,
            date: historyRow?.date ?? fallbackDate,
            sourceType: historyRow?.sourceType ?? "",
            sourceIds: selectedSourceId ? [selectedSourceId] : undefined,
            actorId: historyRow?.actorId,
            organizationActorId: historyRow?.historyItem.organizationActorId,
            changeTypes: historyRow?.changeTypes,
            page: versionsPage,
            limit: 100,
            showReverted: getShowRevertedParam(historyRow),
            options: {
                enabled:
                    shouldShowContent && !!selectedSourceId && !!historyRow,
            },
        });

    const versionGroups = useMemo(() => {
        if (!versionsResponse || !isHistoryDetailsResponse(versionsResponse)) {
            return new Map<string, HistoryDetailsItem[]>();
        }
        return groupByKey(versionsResponse.data, (log) =>
            getBatchKey(log.createdAt),
        );
    }, [versionsResponse]);

    const versionRows = useMemo<JournalEntryVersionRow[]>(() => {
        const rows: JournalEntryVersionRow[] = [];
        for (const [batchKey, logs] of versionGroups.entries()) {
            const firstLog = logs[0];
            const data = extractJournalEntryData(firstLog, {
                preferDiffLines: true,
            });
            rows.push({
                id: batchKey,
                batchKey,
                date: new Date(firstLog.createdAt),
                linesCount: data.linesCount,
                reference: data.reference,
                amount: data.amount,
                originalAmount: data.originalAmount,
                entityId: firstLog.entityId ?? null,
            });
        }
        return rows.toSorted((a, b) => b.date.getTime() - a.date.getTime());
    }, [versionGroups]);

    const filteredVersionRows = useMemo(() => {
        if (!versionsSearch) {
            return versionRows;
        }
        const term = versionsSearch.toLowerCase();
        return versionRows.filter(
            (row) =>
                row.reference.toLowerCase().includes(term) ||
                String(row.linesCount).includes(term),
        );
    }, [versionRows, versionsSearch]);

    const versionsTotalPages = useMemo(() => {
        if (!versionsResponse || !isHistoryDetailsResponse(versionsResponse)) {
            return 1;
        }
        return versionsResponse.totalPages;
    }, [versionsResponse]);

    const shouldSkipVersions = useMemo(() => {
        if (!versionsResponse || !isHistoryDetailsResponse(versionsResponse)) {
            return false;
        }
        if (versionsResponse.totalPages > 1) {
            return false;
        }
        const hasUpdate = versionsResponse.data.some(
            (log) => log.changeType === AuditLogChangeType.UPDATE,
        );
        if (!hasUpdate) {
            return true;
        }
        return versionGroups.size <= 1;
    }, [versionsResponse, versionGroups]);

    useEffect(() => {
        if (
            selectedSourceId &&
            !selectedBatchKey &&
            shouldSkipVersions &&
            versionRows.length > 0
        ) {
            setSelectedBatchKey(versionRows[0].batchKey);
        }
    }, [selectedSourceId, selectedBatchKey, shouldSkipVersions, versionRows]);

    const selectedVersionLogs = useMemo<HistoryDetailsItem[]>(() => {
        if (!selectedBatchKey) {
            return [];
        }
        return versionGroups.get(selectedBatchKey) ?? [];
    }, [selectedBatchKey, versionGroups]);

    const handleClose = useCallback(() => {
        setParentSearch("");
        setVersionsSearch("");
        setSelectedSourceId(null);
        setSelectedBatchKey(null);
        setParentPage(1);
        setVersionsPage(1);
        closeDrawer();
    }, [closeDrawer]);

    const handleSelectParent = useCallback((row?: JournalEntryListRow) => {
        if (!row) {
            return;
        }
        setSelectedSourceId(row.sourceId);
        setVersionsSearch("");
        setVersionsPage(1);
    }, []);

    const handleCloseVersions = useCallback(() => {
        setSelectedSourceId(null);
        setVersionsSearch("");
        setVersionsPage(1);
    }, []);

    const handleSelectVersion = useCallback((row?: JournalEntryVersionRow) => {
        if (!row) {
            return;
        }
        setSelectedBatchKey(row.batchKey);
    }, []);

    const handleCloseDetails = useCallback(() => {
        setSelectedBatchKey(null);
        if (shouldSkipVersions) {
            setSelectedSourceId(null);
            setVersionsSearch("");
            setVersionsPage(1);
        }
    }, [shouldSkipVersions]);

    const detailsBreadcrumbSteps = useMemo(() => {
        if (shouldSkipVersions) {
            return [
                { id: "parent" as const, label: "Journal entries" },
                { id: "details" as const, label: "Details" },
            ];
        }
        return JOURNAL_ENTRY_BREADCRUMB_STEPS;
    }, [shouldSkipVersions]);

    const handleBreadcrumbStepChange = useCallback(
        (stepId: "parent" | "versions" | "details") => {
            if (stepId === "parent") {
                setSelectedSourceId(null);
                setSelectedBatchKey(null);
                setVersionsSearch("");
                setVersionsPage(1);
            } else if (stepId === "versions") {
                handleCloseDetails();
            }
        },
        [handleCloseDetails],
    );

    const layer1HeaderAddonLeft = useMemo(() => {
        if (!historyRow) {
            return null;
        }

        const template = getSourceTypeTemplate(
            historyRow.sourceType,
            historyRow.resourceType,
            historyRow.fields,
        );
        const count = historyRow.count;
        const changeType = historyRow.changeTypes[0];
        const baseVerb = shouldOverrideIntegrationVerb(
            historyRow.sourceType,
            historyRow.changeTypes,
        )
            ? "Updated"
            : (template?.verb ?? "Updated");
        const verb = resolveChangeTypeVerb(
            changeType,
            historyRow.sourceType,
            baseVerb,
        );
        const noun = template?.noun ?? "journal entry";
        const description = `${verb} ${pluralizeWithCount(noun, count)}`;

        return (
            <ActivityDrawerHeader
                historyRow={historyRow}
                activityTitle={description}
            />
        );
    }, [historyRow]);

    const entities = useEntitiesById();
    const { openOverlay } = useOverlays();

    const selectedEntity = useMemo(() => {
        if (!selectedSourceId) {
            return null;
        }
        const logs = logsBySourceId.get(selectedSourceId);
        const entityId = logs?.[0]?.entityId;
        if (!entityId) {
            return null;
        }
        const found = entities.get(entityId);
        if (!found) {
            return null;
        }
        return { id: found.id, legalType: found.legalType, name: found.name };
    }, [selectedSourceId, logsBySourceId, entities]);

    const handleViewJournalEntry = useCallback(() => {
        if (!selectedEntity || !selectedSourceId) {
            return;
        }
        openOverlay("journalEntryDrawer", {
            entity: selectedEntity,
            journalEntryId: selectedSourceId,
        });
    }, [selectedEntity, selectedSourceId, openOverlay]);

    const renderViewOriginalButton = (size: "md" | "lg") => {
        if (!selectedSourceId || !selectedEntity) {
            return undefined;
        }
        return (
            <Tooltip content="View journal entry" side="bottom">
                <Button
                    iconOnly
                    variant="primary"
                    size={size}
                    onClick={handleViewJournalEntry}
                    aria-label="View journal entry"
                >
                    <ArrowCornerIcon />
                </Button>
            </Tooltip>
        );
    };

    const renderLayer1Content = () => {
        if (isParentsLoading) {
            return (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            );
        }

        if (filteredParentRows.length === 0) {
            return (
                <div className={styles.emptyContainer}>
                    <EmptyState
                        header={
                            parentSearch
                                ? "No matching journal entries found"
                                : "No journal entries found"
                        }
                    />
                </div>
            );
        }

        const footer = (
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <div className={styles.footerLeft}>
                    {parentTotalPages > 1 && (
                        <Pagination
                            size="md"
                            currentPage={parentPage}
                            pageCount={parentTotalPages}
                            onPageChange={setParentPage}
                        />
                    )}
                    <CommandIslandTotal>
                        {pluralizeWithCount(
                            "journal entry",
                            filteredParentRows.length,
                        )}
                    </CommandIslandTotal>
                </div>
                <div />
            </CommandIsland>
        );

        return (
            <ViewTable
                data={filteredParentRows}
                view={parentView}
                config={journalEntryListViewConfig}
                onViewChange={setParentView}
                onRowClick={handleSelectParent}
                emptyView={() => (
                    <EmptyState header="No journal entries found" />
                )}
                footer={footer}
                className={styles.historyDetailsView}
            />
        );
    };

    const renderLayer2Content = () => {
        if (isVersionsLoading || (shouldSkipVersions && !selectedBatchKey)) {
            return (
                <div className={styles.loaderContainer}>
                    <Loader />
                </div>
            );
        }

        if (filteredVersionRows.length === 0) {
            return (
                <div className={styles.emptyContainer}>
                    <EmptyState
                        header={
                            versionsSearch
                                ? "No matching versions found"
                                : "No versions found"
                        }
                    />
                </div>
            );
        }

        const showVersionsPagination = versionsTotalPages > 1;
        const footer = (
            <CommandIsland
                sticky
                className={commandIslandStyles.bulkActionsStickyFooter}
            >
                <div className={styles.footerLeft}>
                    {showVersionsPagination && (
                        <Pagination
                            size="md"
                            currentPage={versionsPage}
                            pageCount={versionsTotalPages}
                            onPageChange={setVersionsPage}
                        />
                    )}
                    <CommandIslandTotal>
                        {pluralizeWithCount(
                            "version",
                            filteredVersionRows.length,
                        )}
                    </CommandIslandTotal>
                </div>
                <div>{renderViewOriginalButton("md")}</div>
            </CommandIsland>
        );

        return (
            <ViewTable
                data={filteredVersionRows}
                view={versionsView}
                config={journalEntryVersionsViewConfig}
                onViewChange={setVersionsView}
                onRowClick={handleSelectVersion}
                emptyView={() => <EmptyState header="No versions found" />}
                footer={footer}
                className={styles.historyDetailsView}
            />
        );
    };

    return (
        <>
            <Drawer
                open={open}
                onClose={handleClose}
                direction="right"
                onAnimationEnd={setIsOpen}
                headerAddonLeft={layer1HeaderAddonLeft}
                size="half"
                noPadding
            >
                {shouldShowContent && historyRow && (
                    <div className={styles.drawerContent}>
                        <div className={styles.toolbar}>
                            <div className={styles.toolbarLeft}>
                                <SearchFilter
                                    filter={parentSearch}
                                    onChange={(v) => setParentSearch(v ?? "")}
                                    placeholder="Search..."
                                    minLength={2}
                                />
                            </div>
                            <ColumnSettings
                                view={parentView}
                                viewConfig={journalEntryListViewConfig}
                                defaultView={defaultJournalEntryListView}
                                onViewChange={setParentView}
                            />
                        </div>
                        <div className={styles.content}>
                            {renderLayer1Content()}
                        </div>
                    </div>
                )}
            </Drawer>

            <Drawer
                open={!!selectedSourceId}
                onClose={handleCloseVersions}
                direction="right"
                size="half"
                noPadding
                headerAddonLeft={
                    <StepTrailBreadcrumbs
                        steps={JOURNAL_ENTRY_BREADCRUMB_STEPS.slice(0, 2)}
                        currentStep="versions"
                        onStepChange={handleBreadcrumbStepChange}
                    />
                }
            >
                {selectedSourceId && (
                    <div className={styles.drawerContent}>
                        <div className={styles.toolbar}>
                            <div className={styles.toolbarLeft}>
                                <SearchFilter
                                    filter={versionsSearch}
                                    onChange={(v) => setVersionsSearch(v ?? "")}
                                    placeholder="Search..."
                                    minLength={2}
                                />
                            </div>
                        </div>
                        <div className={styles.content}>
                            {renderLayer2Content()}
                        </div>
                    </div>
                )}
            </Drawer>

            <Drawer
                open={!!selectedBatchKey}
                onClose={handleCloseDetails}
                direction="right"
                size="fullScreen"
                noPadding
                headerAddonLeft={
                    <StepTrailBreadcrumbs
                        steps={detailsBreadcrumbSteps}
                        currentStep="details"
                        onStepChange={handleBreadcrumbStepChange}
                    />
                }
                headerAddonBeforeClose={renderViewOriginalButton("md")}
            >
                {selectedBatchKey && selectedVersionLogs.length > 0 && (
                    <HistoryJournalEntryDetails
                        data={selectedVersionLogs}
                        footer={null}
                    />
                )}
            </Drawer>
        </>
    );
};
